import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { usePlatformScope } from "@/context/PlatformScope";
import { formatApiErrorDetail } from "@/lib/api";
import { TenantsAPI, InstitutionsAPI } from "@/lib/sprint2-api";
import { UsersAdminAPI, StaffAPI } from "@/lib/sprint3-api";
import { NoticesAPI } from "@/lib/sprint7-api"; // reuse GridFS upload for photos
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Plus, Loader2, Search, Camera, AlertCircle, Pencil, Trash2, CheckCircle2, Phone, Briefcase, ShieldCheck, Download } from "lucide-react";

const STAFF_ROLE_OPTIONS = [
  { value: "inst_admin", label: "Institution Admin" },
  { value: "principal", label: "Principal" },
  { value: "coordinator", label: "Coordinator" },
  { value: "hod", label: "Head of Department" },
  { value: "class_teacher", label: "Class Teacher" },
  { value: "teacher", label: "Teacher" },
  { value: "non_teaching", label: "Non-Teaching Staff" },
  { value: "lab_asst_physics", label: "Lab Assistant — Physics" },
  { value: "lab_asst_chemistry", label: "Lab Assistant — Chemistry" },
  { value: "lab_asst_biology", label: "Lab Assistant — Biology" },
  { value: "lab_asst_computer", label: "Lab Assistant — Computer" },
  { value: "it_admin", label: "IT" },
  { value: "finance", label: "Finance" },
  { value: "accountant", label: "Accountant" },
  { value: "librarian", label: "Librarian" },
];
const STAFF_ROLE_SET = new Set(STAFF_ROLE_OPTIONS.map((r) => r.value).concat(["super_admin", "campus_admin"]));
const STUDENT_LIKE = new Set(["student", "parent"]);

function avatarInitials(name = "") {
  return (name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("") || "?").toUpperCase();
}

function roleLabel(r) {
  return STAFF_ROLE_OPTIONS.find((o) => o.value === r)?.label || r.replace(/_/g, " ");
}

function isKycComplete(u) {
  return !!u.photo_url && !!u.teacher_id && !!u.phone && !!u.first_name && !!u.last_name && !!u.aadhaar_no && !!u.pan_no;
}

export default function Staff() {
  const { user } = useAuth();
  const isSuper = (user?.roles || []).includes("super_admin");
  const { institutionId: scopedInstId, campusId: scopedCampusId } = usePlatformScope();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();

  const tenantsQ = useQuery({
    queryKey: ["staff-tenants"],
    queryFn: () => TenantsAPI.list(),
    enabled: isSuper,
  });

  // When a super admin has picked a scoped institution, look up its tenant so
  // the staff list is fetched from the right tenant partition. Without this,
  // the page defaulted to the first tenant in the account which never matches
  // the header's PlatformScope selection for multi-tenant setups.
  const scopedInstQ = useQuery({
    queryKey: ["staff-scoped-inst", scopedInstId],
    queryFn: () => InstitutionsAPI.list().then((rows) => rows.find((i) => i.id === scopedInstId)),
    enabled: isSuper && !!scopedInstId,
  });
  const tenantId = isSuper
    ? (scopedInstQ.data?.tenant_id || tenantsQ.data?.[0]?.id)
    : user?.tenant_id;
  const tenantParam = tenantId ? { tenant_id: tenantId } : {};

  const usersQ = useQuery({
    // Include scope in the cache key so the list re-fetches when the admin
    // switches campus/institution in the header dropdown.
    queryKey: ["staff-users", tenantId, scopedInstId, scopedCampusId],
    queryFn: () => UsersAdminAPI.list({ ...tenantParam, limit: 500 }),
    enabled: !!tenantId,
  });

  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [roleFilter, setRoleFilter] = useState("all");

  const staff = useMemo(() => {
    const list = (usersQ.data || []).filter((u) => {
      const roles = u.roles || [];
      // Exclude pure students/parents
      if (roles.length && roles.every((r) => STUDENT_LIKE.has(r))) return false;
      return true;
    });
    return list.map((u) => ({
      ...u,
      _displayName: [u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || u.email,
      _primaryRole: (u.roles || []).find((r) => STAFF_ROLE_SET.has(r)) || (u.roles || [])[0] || "staff",
      _kyc: isKycComplete(u),
    }));
  }, [usersQ.data]);

  const counts = useMemo(() => {
    const c = { all: staff.length };
    for (const u of staff) {
      const r = u._primaryRole;
      c[r] = (c[r] || 0) + 1;
    }
    return c;
  }, [staff]);

  const filtered = useMemo(() => {
    let list = staff;
    if (roleFilter !== "all") list = list.filter((u) => u._primaryRole === roleFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((u) =>
        u._displayName.toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.phone || "").toLowerCase().includes(q) ||
        (u.teacher_id || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [staff, roleFilter, search]);

  const deleteUser = useMutation({
    mutationFn: (id) => UsersAdminAPI.remove(id),
    onSuccess: () => { toast.success("Staff member removed"); qc.invalidateQueries({ queryKey: ["staff-users"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e)),
  });

  return (
    <AppShell>
      <div className="p-8" data-testid="staff-page">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="font-display font-extrabold tracking-tight text-4xl">Staff Management</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              {counts.all} total · {Object.entries(counts).filter(([k]) => k !== "all").map(([k, n]) => `${n} ${roleLabel(k).toLowerCase()}`).join(" · ")}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              data-testid="staff-download-bank-qr-sheet"
              onClick={async () => {
                try {
                  const { api } = await import("@/lib/api");
                  const params = new URLSearchParams();
                  if (tenantId) params.set("tenant_id", tenantId);
                  const r = await api.get(`/staff/bank-qr-sheet.pdf?${params.toString()}`, { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "bank-qr-sheet.pdf";
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Bank QR sheet downloaded");
                } catch (e) {
                  const msg = e.response?.status === 404 ? "No staff have complete bank details yet" : formatApiErrorDetail(e.response?.data?.detail) || "Download failed";
                  toast.error(msg);
                }
              }}
            >
              <Download className="h-3.5 w-3.5 mr-1.5" /> Payroll QR Sheet
            </Button>
            <CreateStaffDialog tenantId={tenantId} onDone={() => qc.invalidateQueries({ queryKey: ["staff-users"] })} />
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card rounded-2xl shadow-sm p-4 mb-4 space-y-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, phone, or Teacher ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="staff-search"
            />
          </div>
        </div>

        {/* Role filter chips */}
        <div className="flex items-center gap-2 flex-wrap mb-4">
          <button
            onClick={() => setRoleFilter("all")}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${roleFilter === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"}`}
            data-testid="role-chip-all"
          >
            All ({counts.all})
          </button>
          {STAFF_ROLE_OPTIONS.map((r) => counts[r.value] ? (
            <button
              key={r.value}
              onClick={() => setRoleFilter(r.value)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${roleFilter === r.value ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"}`}
              data-testid={`role-chip-${r.value}`}
            >
              {r.label} ({counts[r.value]})
            </button>
          ) : null)}
        </div>

        {/* Staff cards */}
        {usersQ.isLoading ? (
          <div className="bg-card rounded-2xl shadow-sm p-8 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground" data-testid="staff-empty">
            No staff members match your search.
          </div>
        ) : (
          <div className="space-y-3" data-testid="staff-list">
            {filtered.map((u) => (
              <StaffCard
                key={u.id}
                user={u}
                onDelete={() => deleteUser.mutate(u.id)}
                tenantId={tenantId}
                refetch={() => qc.invalidateQueries({ queryKey: ["staff-users"] })}
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

// ─── Staff Card ─────────────────────────────────────────────────────────────
function StaffCard({ user, onDelete, tenantId, refetch }) {
  const [profileOpen, setProfileOpen] = useState(false);
  return (
    <>
    <div
      className="bg-card rounded-2xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-all cursor-pointer group"
      data-testid={`staff-card-${user.id}`}
      onClick={() => setProfileOpen(true)}
    >
      {/* Avatar with gradient ring + online dot */}
      <div className="relative shrink-0">
        <div
          className={`h-14 w-14 rounded-full flex items-center justify-center overflow-hidden font-display font-bold text-base ring-[3px] ${user._kyc ? "ring-emerald-200" : "ring-rose-200"} bg-gradient-to-br ${user._kyc ? "from-emerald-100 to-emerald-50 text-emerald-800" : "from-rose-100 to-rose-50 text-rose-800"}`}
          style={user.photo_url ? { backgroundImage: `url(${user.photo_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
          data-testid={`staff-avatar-${user.id}`}
        >
          {!user.photo_url && avatarInitials(user._displayName)}
        </div>
        {/* tiny online/offline dot */}
        <div className={`absolute -bottom-0.5 left-0.5 h-3.5 w-3.5 rounded-full border-2 border-card ${user.is_active ? "bg-emerald-500" : "bg-gray-400"}`} />
        {!user._kyc && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="absolute -top-1 -right-1 h-5 w-5 bg-rose-500 text-white rounded-full flex items-center justify-center cursor-help shadow" data-testid={`kyc-pending-${user.id}`}>
                  <AlertCircle className="h-3 w-3" />
                </div>
              </TooltipTrigger>
              <TooltipContent>KYC completion is due</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Identity */}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[15px] tracking-tight group-hover:text-primary transition">{user._displayName}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {user.designation || roleLabel(user._primaryRole)} {user.designation && <>· {roleLabel(user._primaryRole)}</>}
        </div>
        <div className="flex items-center gap-2 mt-1.5 text-[11px] text-muted-foreground/80 font-mono">
          {user.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{user.phone}</span>}
          {user.teacher_id && <span>{user.teacher_id}</span>}
          <Badge variant="outline" className="text-[10px] capitalize font-sans bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50">{roleLabel(user._primaryRole)}</Badge>
        </div>
      </div>

      {/* Status + actions */}
      <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
        <Badge className={user.is_active ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 rounded-full px-3" : "bg-gray-100 text-gray-600 rounded-full px-3"}>
          {user.is_active ? "active" : "inactive"}
        </Badge>
        <EditStaffDialog user={user} tenantId={tenantId} onDone={refetch} />
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:bg-destructive/10 rounded-full h-9 w-9 p-0"
          onClick={() => {
            if (window.confirm(`Delete ${user._displayName}? This cannot be undone.`)) onDelete();
          }}
          data-testid={`staff-delete-${user.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
    <StaffProfileDialog user={user} open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
}

function StaffProfileDialog({ user, open, onOpenChange }) {
  // Enrich with the /staff record (extended HR fields) when the dialog opens.
  // The /staff endpoint returns fields like DOB, blood group, banking, KYC,
  // parents' names — all sourced from the bulk-imported CSV.
  const enrichQ = useQuery({
    queryKey: ["staff-enrich", user?.id, user?.email],
    queryFn: async () => {
      if (!user?.email || !user?.tenant_id) return null;
      const rows = await StaffAPI.list({ tenant_id: user.tenant_id, q: user.email });
      // /staff returns merged list — pick the row that matches by user_id when we have it
      return (rows || []).find((r) => r.user_id === user.id || r.email === user.email) || null;
    },
    enabled: !!(open && user?.tenant_id),
    staleTime: 30_000,
  });
  const s = enrichQ.data || {};

  const line = (label, val) => (
    <div><span className="text-muted-foreground">{label}:</span> <span className="font-medium">{val || "—"}</span></div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="staff-profile-dialog">
        <DialogHeader>
          <div className="flex flex-col items-center text-center gap-2">
            <div className="h-24 w-24 rounded-full overflow-hidden ring-4 ring-emerald-200 bg-gradient-to-br from-emerald-100 to-emerald-50 flex items-center justify-center font-display font-bold text-3xl text-emerald-800"
                 style={user.photo_url ? { backgroundImage: `url(${user.photo_url})`, backgroundSize: "cover" } : undefined}>
              {!user.photo_url && avatarInitials(user._displayName)}
            </div>
            <DialogTitle className="text-2xl font-display">{user._displayName}</DialogTitle>
            <div className="text-sm text-muted-foreground">{s.designation || user.designation || roleLabel(user._primaryRole)}{s.department || user.department ? <> · {s.department || user.department}</> : null}</div>
          </div>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <div className="bg-card rounded-2xl shadow-sm p-4">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Phone className="h-4 w-4 text-primary" /> Contact</h4>
            <div className="text-xs space-y-1.5">
              {line("Email", user.email)}
              {line("Mobile", s.phone || user.phone)}
              {line("Alt phone", s.contact_no)}
              {line("Address", s.address)}
            </div>
          </div>
          <div className="bg-card rounded-2xl shadow-sm p-4">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Briefcase className="h-4 w-4 text-primary" /> Employment</h4>
            <div className="text-xs space-y-1.5">
              {line("Staff ID", s.employee_id || user.teacher_id)}
              {line("Role", roleLabel(user._primaryRole))}
              {line("Designation", s.designation || user.designation)}
              {line("Department", s.department)}
              {line("Branch", s.branch)}
              {line("Joined", s.hire_date)}
              {line("Qualification", s.qualification)}
              {line("Experience", s.experience)}
              <div><span className="text-muted-foreground">Status:</span> <span className="font-medium">{user.is_active ? "Active" : "Inactive"}</span></div>
            </div>
          </div>
          <div className="bg-card rounded-2xl shadow-sm p-4">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> Personal</h4>
            <div className="text-xs space-y-1.5">
              {line("Gender", s.gender)}
              {line("D.O.B", s.dob)}
              {line("Blood group", s.blood_group)}
              {line("Father", s.father_name)}
              {line("Mother", s.mother_name)}
            </div>
          </div>
          <div className="bg-card rounded-2xl shadow-sm p-4">
            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" /> KYC &amp; Banking</h4>
            <div className="text-xs space-y-1.5">
              <div><span className="text-muted-foreground">Aadhaar:</span> <span className="font-mono font-medium">{s.aadhaar_no || user.aadhaar_no || <span className="text-rose-600">Pending</span>}</span></div>
              <div><span className="text-muted-foreground">PAN:</span> <span className="font-mono font-medium">{s.pan_no || user.pan_no || <span className="text-rose-600">Pending</span>}</span></div>
              {line("Bank A/C", s.bank_account_no)}
              {line("IFSC", s.ifsc_code)}
              {line("Bank", s.bank_name)}
              <div className="pt-1"><Badge className={user._kyc ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-rose-100 text-rose-700 hover:bg-rose-100"}>{user._kyc ? "KYC Verified" : "KYC Pending"}</Badge></div>
              {s.id && s.bank_account_no && s.ifsc_code && (
                <BankQR staffId={s.id} tenantId={user.tenant_id} beneficiaryName={user._displayName} />
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Bank QR — payroll-friendly scannable card for the staff banking block ─
function BankQR({ staffId, beneficiaryName }) {
  const [open, setOpen] = useState(false);
  // Auth flows through the axios instance which appends the Bearer token —
  // fetch as blob so we can render it in <img src=blobUrl> without exposing
  // the raw URL (which requires auth) in the DOM.
  const { data: blobUrl } = useQuery({
    queryKey: ["staff-bank-qr", staffId, open],
    queryFn: async () => {
      const { api } = await import("@/lib/api");
      const r = await api.get(`/staff/${staffId}/bank-qr.png`, { responseType: "blob" });
      return URL.createObjectURL(new Blob([r.data], { type: "image/png" }));
    },
    enabled: open && !!staffId,
    staleTime: 60_000,
  });

  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = `bank-qr-${(beneficiaryName || "staff").replace(/\s+/g, "-").toLowerCase()}.png`;
    a.click();
  };

  return (
    <div className="pt-2 border-t border-border/70 mt-2">
      {!open ? (
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={() => setOpen(true)}
          data-testid="staff-bank-qr-toggle"
        >
          Show payroll QR
        </Button>
      ) : (
        <div className="flex items-center gap-3">
          {blobUrl ? (
            <img
              src={blobUrl}
              alt="Bank transfer QR"
              className="w-32 h-32 rounded-md border border-border bg-white p-1"
              data-testid="staff-bank-qr-image"
            />
          ) : (
            <div className="w-32 h-32 rounded-md border border-border flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
          <div className="flex flex-col gap-1">
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={download} disabled={!blobUrl} data-testid="staff-bank-qr-download">
              Download PNG
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setOpen(false)}>
              Hide
            </Button>
            <p className="text-[10px] text-muted-foreground max-w-[180px] leading-relaxed">
              Scan with any camera app to read A/C + IFSC. Not a UPI QR.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Create Staff Dialog ────────────────────────────────────────────────────
function CreateStaffDialog({ tenantId, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-staff-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Staff</Button>
      </DialogTrigger>
      <StaffForm
        title="Onboard a staff member"
        submitLabel="Create"
        tenantId={tenantId}
        onSuccess={() => { setOpen(false); onDone?.(); }}
      />
    </Dialog>
  );
}

function EditStaffDialog({ user, tenantId, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`staff-edit-${user.id}`}>
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <StaffForm
        title="Edit staff member"
        submitLabel="Save changes"
        tenantId={tenantId}
        existing={user}
        onSuccess={() => { setOpen(false); onDone?.(); }}
      />
    </Dialog>
  );
}

// ─── Shared form body ───────────────────────────────────────────────────────
function StaffForm({ title, submitLabel, tenantId, onSuccess, existing }) {
  const [firstName, setFirstName] = useState(existing?.first_name || "");
  const [lastName, setLastName] = useState(existing?.last_name || "");
  const [email, setEmail] = useState(existing?.email || "");
  const [phone, setPhone] = useState(existing?.phone || "");
  const [role, setRole] = useState(((existing?.roles || []).find((r) => STAFF_ROLE_SET.has(r))) || "teacher");
  const [designation, setDesignation] = useState(existing?.designation || "");
  const [teacherId, setTeacherId] = useState(existing?.teacher_id || "");
  const [aadhaar, setAadhaar] = useState(existing?.aadhaar_no || "");
  const [pan, setPan] = useState(existing?.pan_no || "");
  const [photoUrl, setPhotoUrl] = useState(existing?.photo_url || "");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const isEdit = !!existing;

  const submit = useMutation({
    mutationFn: () => {
      const payload = {
        first_name: firstName.trim(),
        last_name: lastName.trim() || undefined,
        name: `${firstName.trim()} ${lastName.trim()}`.trim(),
        email: email.trim().toLowerCase() || undefined,
        phone: phone.trim(),
        roles: [role],
        designation: designation.trim() || undefined,
        teacher_id: teacherId.trim() || undefined,
        aadhaar_no: aadhaar.trim() || undefined,
        pan_no: pan.trim().toUpperCase() || undefined,
        photo_url: photoUrl || undefined,
        tenant_id: tenantId,
      };
      if (isEdit) return UsersAdminAPI.update(existing.id, payload);
      return UsersAdminAPI.create(payload);
    },
    onSuccess: () => { toast.success(isEdit ? "Staff updated" : "Staff created — invite sent"); onSuccess?.(); },
    onError: (e) => toast.error(formatApiErrorDetail(e)),
  });

  const onPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const res = await NoticesAPI.uploadAttachment(file);
      setPhotoUrl(NoticesAPI.attachmentUrl(res.id));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const canSubmit = firstName.trim() && phone.trim() && role && !submit.isPending && !uploadingPhoto;

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          Name and mobile number are required. Email is optional now — if you provide one the staff can sign in with it; otherwise their account is created in a login-disabled state until an admin adds an email.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {/* Photo */}
        <div className="flex items-center gap-4">
          <label className="relative group cursor-pointer">
            <div
              className="h-20 w-20 rounded-full border-2 border-dashed border-border flex items-center justify-center overflow-hidden bg-muted/30"
              style={photoUrl ? { backgroundImage: `url(${photoUrl})`, backgroundSize: "cover", backgroundPosition: "center", borderStyle: "solid" } : undefined}
            >
              {!photoUrl && <Camera className="h-6 w-6 text-muted-foreground" />}
              {uploadingPhoto && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Loader2 className="h-5 w-5 text-white animate-spin" /></div>}
            </div>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              onChange={(e) => onPhoto(e.target.files?.[0])}
              data-testid="staff-photo-input"
            />
          </label>
          <div>
            <div className="text-sm font-medium">Profile photo</div>
            <div className="text-xs text-muted-foreground">Click the circle to upload. KYC complete = green ring.</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>First name *</Label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="staff-first-name" />
          </div>
          <div>
            <Label>Last name</Label>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="staff-last-name" />
          </div>
          <div>
            <Label>Mobile number *</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 9999999999" data-testid="staff-phone" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isEdit} placeholder="staff@stpeters.demo" data-testid="staff-email" />
            <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" /> Optional — required only for sign-in. Leave blank if the staff won&apos;t use the portal.
            </div>
          </div>
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAFF_ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Designation</Label>
            <Input value={designation} onChange={(e) => setDesignation(e.target.value)} placeholder="e.g. Senior Maths Faculty" data-testid="staff-designation" />
          </div>
          <div className="col-span-2">
            <Label>Teacher / Staff ID</Label>
            <Input value={teacherId} onChange={(e) => setTeacherId(e.target.value)} placeholder="e.g. SPS-T-001" data-testid="staff-teacher-id" />
            <div className="text-[10px] text-muted-foreground mt-1">Optional — but required later for a green KYC badge on the roster.</div>
          </div>
          <div>
            <Label>Aadhaar No.</Label>
            <Input value={aadhaar} onChange={(e) => setAadhaar(e.target.value)} maxLength={14} placeholder="XXXX XXXX XXXX" data-testid="staff-aadhaar" />
          </div>
          <div>
            <Label>PAN Card</Label>
            <Input value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} maxLength={10} placeholder="ABCDE1234F" data-testid="staff-pan" />
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button onClick={() => submit.mutate()} disabled={!canSubmit} data-testid="staff-submit">
          {submit.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {submitLabel}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
